import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'lib-otp-input',
  imports: [CommonModule],
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OtpInputComponent),
      multi: true
    }
  ]
})
export class OtpInputComponent implements ControlValueAccessor, OnInit {
  @Input() length = 6;
  @Output() codeCompleted = new EventEmitter<string>();

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  digitValues: string[] = [];
  touchedDigits: boolean[] = [];
  disabled = () => false;

  private onChange: any = () => {};
  private onTouched: any = () => {};

  ngOnInit() {
    this.digitValues = Array(this.length).fill('');
    this.touchedDigits = Array(this.length).fill(false);
  }

  onDigitInput(event: Event, index: number) {
    const inputEl = event.target as HTMLInputElement;
    const char = (inputEl.value || '').replace(/[^0-9]/g, '')[0] || '';

    this.digitValues[index] = char;
    this.touchedDigits[index] = true;

    if (char && index < this.length - 1) {
      setTimeout(() => this.otpInputs.toArray()[index + 1].nativeElement.focus(), 0);
    }

    this.emitValue();
    inputEl.value = char;
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace') {
      this.touchedDigits[index] = true;
      if (this.digitValues[index]) {
        this.digitValues[index] = '';
      } else if (index > 0) {
        this.otpInputs.toArray()[index - 1].nativeElement.focus();
        this.digitValues[index - 1] = '';
        this.touchedDigits[index - 1] = true;
      }
      this.emitValue();
      event.preventDefault();
    }
  }

  onBlur(index: number) {
    this.touchedDigits[index] = true;
  }

  isInvalid(index: number) {
    return this.touchedDigits[index] && !this.digitValues[index];
  }

  emitValue() {
    const code = this.digitValues.join('');
    this.onChange(code);
    if (code.length === this.length && !this.digitValues.includes('')) {
      this.codeCompleted.emit(code);
    }
  }

  writeValue(value: any): void {
    if (value) {
      this.digitValues = value.split('').slice(0, this.length);
      this.touchedDigits = this.digitValues.map(() => true);
    } else {
      this.digitValues = Array(this.length).fill('');
      this.touchedDigits = Array(this.length).fill(false);
    }
  }
  onFocus(index: number) {
    const inputEl = this.otpInputs.toArray()[index].nativeElement;
    setTimeout(() => inputEl.setSelectionRange(0, 0), 0);
  }
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').split('');

    if (!digits.length) return;

    const startIndex = this.otpInputs.toArray().findIndex(
      input => input.nativeElement === document.activeElement
    );

    let currentIndex = startIndex >= 0 ? startIndex : 0;

    for (const d of digits) {
      if (currentIndex >= this.length) break;
      this.digitValues[currentIndex] = d;
      this.touchedDigits[currentIndex] = true;
      currentIndex++;
    }

    this.emitValue();
    if (currentIndex < this.length) {
      this.otpInputs.toArray()[currentIndex].nativeElement.focus();
    } else {
      this.otpInputs.toArray()[this.length - 1].nativeElement.blur();
    }
  }
  clear() {
    this.digitValues = Array(this.length).fill('');
    this.touchedDigits = Array(this.length).fill(false);
    this.onChange('');
    this.onTouched();
    this.otpInputs?.forEach(input => input.nativeElement.value = '');
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = () => isDisabled; }
}
